/**
 * Meta Integration OAuth Scopes (Story 9.1, Task 1.2)
 *
 * Maps each Meta platform to its required permission(s).
 * Meta requests ALL scopes in a single consent — no per-platform selection like Google.
 *
 * instagram-ads maps to same scopes as facebook-ads (ads_read, business_management) —
 * it's Meta Ads with publisher_platform='instagram'.
 */

import type { Platform } from "@provenleap/db";

// ============================================================================
// Types
// ============================================================================

export interface MetaScopeConfig {
  /** Meta permission name(s) required for this platform */
  readonly scopes: readonly string[];
}

/**
 * Meta platform values for validation.
 */
export const META_PLATFORM_VALUES = [
  "facebook-pages",
  "facebook-ads",
  "instagram-business",
  "instagram-ads",
] as const;

export type MetaPlatform = (typeof META_PLATFORM_VALUES)[number];

// ============================================================================
// Scope Mapping
// ============================================================================

/**
 * Maps each Meta platform to its required permission(s).
 *
 * Meta uses permission names (not URLs like Google).
 * All permissions require Meta App Review approval before production use.
 */
export const META_INTEGRATION_SCOPES: Record<MetaPlatform, MetaScopeConfig> = {
  "facebook-pages": {
    scopes: ["pages_show_list", "pages_read_engagement"],
  },
  "facebook-ads": {
    scopes: ["ads_read", "business_management"],
  },
  "instagram-business": {
    scopes: ["pages_show_list", "instagram_basic", "instagram_manage_insights"],
  },
  "instagram-ads": {
    // Same as facebook-ads — Instagram Ads uses Meta Marketing API with publisher_platform='instagram'
    scopes: ["ads_read", "business_management"],
  },
} as const;

/** All Meta platforms that use OAuth */
export const META_OAUTH_PLATFORMS = Object.keys(
  META_INTEGRATION_SCOPES
) as Platform[];

/** All unique Meta scopes as a flat deduplicated array */
export const ALL_META_SCOPES = [
  ...new Set(
    Object.values(META_INTEGRATION_SCOPES).flatMap((config) => config.scopes)
  ),
];

/**
 * Meta Graph API version used for all OAuth and API calls.
 * Updated per Meta's versioning schedule — check breaking changes before bumping.
 */
export const META_GRAPH_API_VERSION = "v25.0";
